import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { authHeaders } from '@/features/ai-chat/core/utils/authHeaders'
import { formatDateTime } from '@/lib/dateUtils'

export interface LatestManifest {
    manifestId: number
    url?: string
    expiresAt?: string
    expired?: boolean
    created?: string
    createdBy?: string | null
    objectCount?: number
    fileCount?: number
    earliestUrlExpiry?: string | null
}

/**
 * Whether a scope already has a manifest, and when its link dies.
 *
 * Read on the list screens so nobody has to open a dialog to find out. During an incident the
 * question "has anyone already generated the URLs, and are they still good?" is asked far more
 * often than the manifest is built, and answering it by opening a dialog and waiting for a
 * status call is the wrong shape.
 *
 * `refreshToken` is bumped by the parent when the dialog closes, since building a manifest in
 * there is exactly what changes the answer out here.
 */
export function useLatestManifest(
    scopeQuery: string | null,
    refreshToken = 0
): { manifest: LatestManifest | null; loading: boolean } {
    const [manifest, setManifest] = useState<LatestManifest | null>(null)
    const [loading, setLoading] = useState(false)

    const load = useCallback(async () => {
        if (!scopeQuery) return
        setLoading(true)
        try {
            const res = await fetch(`/api/recovery-manifest/latest?${scopeQuery}`, { headers: authHeaders() })
            if (!res.ok) { setManifest(null); return }
            const body = await res.json()
            setManifest((body?.data ?? body) || null)
        } catch {
            // A list screen showing no badge is a better failure than one showing an error.
            setManifest(null)
        } finally {
            setLoading(false)
        }
    }, [scopeQuery])

    useEffect(() => { void load() }, [load, refreshToken])
    return { manifest, loading }
}

/** Short, human expiry — "in 4h", "in 25m", or the date once it is further out than a day. */
function untilLabel(when: string, locale: string): string {
    const ms = new Date(when).getTime() - Date.now()
    if (ms <= 0) return ''
    const mins = Math.round(ms / 60000)
    if (mins < 60) return `${mins}m`
    const hours = Math.round(mins / 60)
    if (hours < 24) return `${hours}h`
    return new Date(when).toLocaleDateString(locale, { day: 'numeric', month: 'short' })
}

/**
 * The badge itself. Renders NOTHING when no manifest has ever been built for the scope —
 * an absent badge is the honest signal, and a "none yet" chip on every row is noise.
 */
export function ManifestStatusBadge({
    manifest,
    className = '',
    showEmpty = false,
}: {
    manifest: LatestManifest | null
    className?: string
    /**
     * Say so when there is no manifest yet. Opt-in, and only for a single header — on a list
     * of 39 rows it is noise, but on one header the difference between "none built" and
     * "the badge is broken" is worth a line.
     */
    showEmpty?: boolean
}) {
    const { t, i18n } = useTranslation()
    if (!manifest) {
        if (!showEmpty) return null
        return (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs text-muted-foreground ${className}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                {t('recovery.manifest.badgeNone', 'No manifest yet')}
            </span>
        )
    }

    if (manifest.expired) {
        return (
            <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground ${className}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60" />
                {t('recovery.manifest.badgeExpired', 'Manifest expired')}
            </span>
        )
    }
    if (!manifest.expiresAt) return null

    const until = untilLabel(manifest.expiresAt, i18n.language || 'en-GB')
    if (!until) return null

    // Amber under an hour: a link about to die mid-recovery is worth noticing before you
    // hand it to someone.
    const soon = new Date(manifest.expiresAt).getTime() - Date.now() < 60 * 60 * 1000
    return (
        <span
            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${soon
                ? 'text-amber-700 dark:text-amber-400'
                : 'text-emerald-700 dark:text-emerald-400'} ${className}`}
            title={t('recovery.manifest.badgeTitle', 'Manifest for {{f}} file(s), link valid until {{when}}', {
                f: manifest.fileCount ?? 0,
                when: formatDateTime(manifest.expiresAt),
            })}
        >
            <span className={`h-1.5 w-1.5 rounded-full ${soon ? 'bg-amber-500' : 'bg-emerald-500'}`} />
            {t('recovery.manifest.badgeValid', 'Manifest {{until}}', { until })}
        </span>
    )
}
